import { useState } from 'react';
import { Title, Stack, Badge, Group, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconCheck, IconEye } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { usePendingModerations } from '@/hooks/useModeration';
import { ModerationOverrideDialog } from './ModerationOverrideDialog';
import { ModerationDetailDrawer } from './ModerationDetailDrawer';
import type { ModerationResult, ModerationDecision } from '@/types/moderation';

const decisionColor: Record<ModerationDecision, string> = {
  ALLOW: 'green',
  REVIEW: 'yellow',
  HOLD: 'red',
};

const sourceLabel: Record<string, string> = {
  RULE_ENGINE: 'Regel',
  AI: 'KI',
  MANUAL: 'Manuell',
};

const reasonMap: Record<string, string> = {
  REVIEW_NO_DESCRIPTION: 'Keine Beschreibung vorhanden',
  REVIEW_SHORT_DESCRIPTION: 'Beschreibung zu kurz',
  REVIEW_SPAM_DETECTED: 'Spam erkannt',
  REVIEW_INAPPROPRIATE_CONTENT: 'Unangemessener Inhalt',
  REVIEW_DUPLICATE: 'Doppeltes Angebot',
  REVIEW_PRICE_SUSPICIOUS: 'Verdächtiger Preis',
  REVIEW_URGENCY_CLAIMS: 'Dringlichkeitsbehauptungen',
  REVIEW_BRAND_NAME: 'Markenname im Titel',
  HOLD_HEALTH_CLAIMS: 'Gesundheitsbezogene Aussagen',
  HOLD_OEM_BRAND: 'Verdacht auf Markenfälschung',
  HOLD_MISSING_MANUFACTURER: 'Fehlende Herstellerangabe',
};

export function reasonLabel(reason: string): string {
  if (!reason) return '—';
  const match = reason.match(/^triggered rule: (.+)$/);
  const key = match?.[1] ?? reason;
  return reasonMap[key] || key;
}

export function ModerationPage() {
  const { data: items = [], isLoading } = usePendingModerations();
  const [selectedItem, setSelectedItem] = useState<ModerationResult | null>(null);
  const [overrideItem, setOverrideItem] = useState<ModerationResult | null>(null);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Moderation</Title>
        <Text c="dimmed" size="sm">{items.length} offene Fälle</Text>
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={items}
        fetching={isLoading}
        idAccessor="id"
        columns={[
          {
            accessor: 'offerId',
            title: 'Offer ID',
            width: 90,
          },
          {
            accessor: 'offer.name',
            title: 'Produktname',
            render: (record) => record.offer?.name || `Offer #${record.offerId}`,
          },
          {
            accessor: 'decision',
            title: 'Entscheidung',
            width: 120,
            render: (record) => (
              <Badge color={decisionColor[record.decision]} variant="light">
                {record.decision}
              </Badge>
            ),
          },
          {
            accessor: 'source',
            title: 'Quelle',
            width: 100,
            render: (record) => sourceLabel[record.source] || record.source,
          },
          {
            accessor: 'reason',
            title: 'Grund',
            ellipsis: true,
            render: (record) => reasonLabel(record.reason),
          },
          {
            accessor: 'createdAt',
            title: 'Datum',
            width: 150,
            render: (record) => new Date(record.createdAt).toLocaleString('de-DE'),
          },
          {
            accessor: 'actions',
            title: '',
            width: 80,
            render: (record) => (
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Details">
                  <ActionIcon variant="subtle" size="sm" onClick={() => setSelectedItem(record)}>
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Freigeben">
                  <ActionIcon variant="subtle" color="green" size="sm" onClick={() => setOverrideItem(record)}>
                    <IconCheck size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ),
          },
        ]}
        onRowClick={({ record }) => setSelectedItem(record)}
      />

      <ModerationDetailDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onOverride={(item) => {
          setSelectedItem(null);
          setOverrideItem(item);
        }}
      />

      <ModerationOverrideDialog
        item={overrideItem}
        onClose={() => setOverrideItem(null)}
      />
    </Stack>
  );
}
